import { phone as formatPhone } from "@repo/helpers";
import { GridColumnTemplateEnum } from "@repo/models";

import { ButtonColor } from "../../basic/button";
import { ButtonColumn } from "./buttonColumn";
import { Column, ColumnPath } from "./column";
import { DateColumn, LocalDateColumn, OriginalTimezoneDateColumn } from "./dateColumn";

/** A grid column as configured in the database (the GridColumns tables). */
export interface GridColumnDefinition<T> {
	gridColumnId: number;
	name: ColumnPath<T>;
	title: string;
	position: number;
	visible: boolean;
	width: number;
	sortable: boolean;
	filterable: boolean;
	exportable?: boolean;
	template?: string;
	templateId?: GridColumnTemplateEnum;
	options?: {
		text?: string | null;
		onClick?: string | null;
		dataType?: string | null;
		dateType?: string | null;
		format?: string | null;
		type?: string;
		options?: string[];
		visible?: string | null;
		color?: ButtonColor;
		/** Link columns: URL with `{name}` replaced by the cell's value. */
		url?: string | null;
	};
}

export type ColumnTemplate<T> = (definition: GridColumnDefinition<T>, options: ColumnDefinitionOptions<T>) => Column<T>;

export interface ColumnDefinitionOptions<T> {
	/** Looked up by `options.onClick` for button columns. */
	callbacks?: Record<string, Function>;
	/** Looked up by `options.visible`; a falsy value hides the column. */
	settings?: Record<string, any>;
	/** Overrides or adds templates by templateId; unlisted ids use the built-in templates. */
	templates?: Partial<Record<GridColumnTemplateEnum, ColumnTemplate<T>>>;
}

export function columnFromDefinition<T>(
	definition: GridColumnDefinition<T>,
	options: ColumnDefinitionOptions<T> = {},
): Column<T> {
	const template = options.templates?.[definition.templateId] ?? defaultTemplates[definition.templateId];
	const column: Column<T> = template
		? template(definition, options)
		: new Column<T>(definition.title, definition.name);

	const visibilitySetting = definition.options?.visible;
	column.withVisibility(definition.visible && (!visibilitySetting || !!options.settings?.[visibilitySetting]));
	if (definition.width) column.withMinWidth(definition.width);
	column.withSortable(definition.sortable);
	column.withExportable(definition.exportable ?? true);

	return column;
}

function dateColumn<T>({ title, name, options }: GridColumnDefinition<T>) {
	switch (options?.dateType) {
		case "original":
			return new OriginalTimezoneDateColumn<T>(title, name);
		case "local":
			return new LocalDateColumn<T>(title, name);
		default:
			return new DateColumn<T>(title, name);
	}
}

function buttonColumn<T>(definition: GridColumnDefinition<T>, { callbacks }: ColumnDefinitionOptions<T>) {
	const { title, name, options } = definition;
	const column = new ButtonColumn<T>(title, name);
	if (options?.text) column.withButtonText(options.text);
	const callback = options?.onClick ? callbacks?.[options.onClick] : undefined;
	if (callback) {
		// A button with no column name, such as Review or Portal, gets the whole row.
		column.withOnClick((row) => callback(name ? name.split(".").reduce((prev, curr) => prev?.[curr], row) : row));
	}
	if (options?.color) column.withButtonColor(options.color);
	return column;
}

const defaultTemplates: Partial<Record<GridColumnTemplateEnum, ColumnTemplate<any>>> = {
	[GridColumnTemplateEnum.Date]: (definition) => dateColumn(definition).dateOnly(),
	[GridColumnTemplateEnum.DateTime]: dateColumn,
	[GridColumnTemplateEnum.Boolean]: ({ title, name, options }) => {
		const [trueValue, falseValue, nullValue] = options?.options ?? [];
		return new Column<any>(title, name).withBooleanFormat(trueValue, falseValue, nullValue);
	},
	[GridColumnTemplateEnum.PhoneNumber]: ({ title, name }) =>
		new Column<any>(title, name).withFormat((value) => formatPhone(value)),
	[GridColumnTemplateEnum.LinkButton]: ({ title, name, options }) =>
		new Column<any>(title, name).asLink(options?.url ?? ""),
	[GridColumnTemplateEnum.Button]: buttonColumn,
	[GridColumnTemplateEnum.Percentage]: ({ title, name }) =>
		new Column<any>(title, name).withFormat((value) => `${((value ?? 0) * 100).toFixed(0)}%`),
};
